import { z } from 'zod';
import { isValidPlatformPlacement, PREFLIGHT_PLACEMENTS, PREFLIGHT_PLATFORMS } from '../../constants/preflight-criteria.js';
import { UploadIdSchema } from '../../constants/upload-id.js';
import { PreflightMarketKindSchema } from '../../preflight/market.js';
import {
  PUBLIC_API_ASSET_STATUSES,
  PUBLIC_API_FEATURE_STATES,
  PUBLIC_API_REPORT_STATUSES,
  PUBLIC_API_SCORE_BANDS,
  PUBLIC_API_UPLOAD_CONFIRM_STATUSES,
} from './constants.js';

export {
  PUBLIC_API_ASSET_STATUSES,
  PUBLIC_API_FEATURE_STATES,
  PUBLIC_API_REPORT_STATUSES,
  PUBLIC_API_SCORE_BANDS,
  PUBLIC_API_TOKEN_EXCHANGE_RATE_LIMIT_PER_MINUTE,
  PUBLIC_API_TOKEN_PREFIX,
  PUBLIC_API_UPLOAD_CONFIRM_STATUSES,
} from './constants.js';
export type {
  PublicApiAssetStatusCode,
  PublicApiReportStatusCode,
} from './constants.js';

export const MAX_ASSETS_PER_SCORING_JOB = 25;

export const PublicPlatformSchema = z.enum(PREFLIGHT_PLATFORMS);

export const PublicPlacementSchema = z.enum(PREFLIGHT_PLACEMENTS);

const HttpsUrlSchema = z
  .string()
  .trim()
  .url('Each URL must be a valid URL')
  .refine((u) => u.startsWith('https://'), 'Only HTTPS URLs are accepted');

export const PublicAuthTokenRequestSchema = z
  .object({
    apiToken: z.string().min(1, 'apiToken is required'),
  })
  .strict();

export const PublicAuthTokenResponseSchema = z.object({
  accessToken: z.string(),
  expiresAt: z.number().int(),
  tokenType: z.literal('Bearer'),
});

export const PublicMarketSchema = z.object({
  id: z.string(),
  kind: PreflightMarketKindSchema,
  name: z.string(),
  code: z.string(),
  reportCount: z.number().int().optional(),
});

export const PublicMarketsResponseSchema = z.object({
  markets: z.array(PublicMarketSchema),
});

export const PublicUploadPresignRequestSchema = z
  .object({
    contentType: z.string().min(1),
    sizeBytes: z.number().int().positive(),
    originalName: z.string().trim().min(1).max(255),
  })
  .strict();

export const PublicUploadPresignResponseSchema = z.object({
  uploadId: z.string(),
  uploadUrl: z.string().url(),
  expiresAt: z.string(),
  requiredHeaders: z.record(z.string()),
});

export const PublicUploadConfirmResponseSchema = z.object({
  uploadId: z.string(),
  status: z.enum(PUBLIC_API_UPLOAD_CONFIRM_STATUSES),
  contentType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
});

export const PublicCreateScoringJobBodySchema = z
  .object({
    uploadIds: z
      .array(UploadIdSchema)
      .default([])
      .refine((ids) => new Set(ids).size === ids.length, 'Duplicate upload IDs are not allowed'),
    urls: z.array(HttpsUrlSchema).default([]),
    platform: PublicPlatformSchema,
    placement: PublicPlacementSchema,
    brandName: z.string().trim().min(1, 'Brand name is required').max(255),
    marketId: z.string().cuid2('A market is required'),
    jobName: z.string().trim().min(1).max(255).optional(),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .strict()
  .refine(
    (b) => b.uploadIds.length + b.urls.length > 0,
    'At least one upload or URL is required',
  )
  .refine(
    (b) => b.uploadIds.length + b.urls.length <= MAX_ASSETS_PER_SCORING_JOB,
    `A job may contain at most ${MAX_ASSETS_PER_SCORING_JOB} assets`,
  )
  .refine(
    (b) => isValidPlatformPlacement(b.platform, b.placement),
    'Placement is not valid for the selected platform',
  );

export const PublicCreateScoringJobResponseSchema = z.object({
  jobId: z.string(),
  status: z.literal('QUEUED'),
});

export const PublicScoringJobStatusSchema = z.object({
  jobId: z.string(),
  status: z.enum(PUBLIC_API_REPORT_STATUSES),
  overallScore: z.number().nullable().optional(),
  overallBand: z.enum(PUBLIC_API_SCORE_BANDS).nullable().optional(),
  assetCount: z.number().int().optional(),
  scoredCount: z.number().int().optional(),
  failedCount: z.number().int().optional(),
});

export const PublicScoringSubCheckSchema = z.object({
  name: z.string(),
  passed: z.boolean().nullable(),
  applicable: z.boolean(),
  detail: z.string().nullable(),
});

export const PublicScoringFeatureSchema = z.object({
  criterionId: z.string(),
  name: z.string(),
  definition: z.string().nullable(),
  displayOrder: z.number().int().nullable(),
  passed: z.boolean(),
  score: z.number().nullable().optional(),
  weight: z.number().nullable().optional(),
  state: z.enum(PUBLIC_API_FEATURE_STATES),
  subChecks: z.array(PublicScoringSubCheckSchema),
});

export const PublicScoringJobAssetSchema = z
  .object({
    id: z.string(),
    uploadId: z.string(),
    status: z.enum(PUBLIC_API_ASSET_STATUSES),
    score: z.number().nullable().optional(),
    band: z.enum(PUBLIC_API_SCORE_BANDS).nullable().optional(),
    error: z.string().nullable().optional(),
    originalName: z.string().nullable().optional(),
    contentType: z.string().nullable().optional(),
    sourceUrl: z.string().nullable().optional(),
    assetUrl: z.string().nullable().optional(),
    thumbnailUrl: z.string().nullable().optional(),
    features: z.array(PublicScoringFeatureSchema),
    summaryText: z.string(),
    summaryHtml: z.string(),
  })
  .strict();

export const PublicScoringJobMarketSchema = z.object({
  id: z.string(),
  kind: PreflightMarketKindSchema,
  name: z.string(),
  code: z.string(),
});

export const PublicScoringJobResultsSchema = z
  .object({
    jobId: z.string(),
    name: z.string(),
    brandName: z.string(),
    platform: PublicPlatformSchema,
    placement: PublicPlacementSchema,
    status: z.enum(PUBLIC_API_REPORT_STATUSES),
    overallScore: z.number().nullable().optional(),
    overallBand: z.enum(PUBLIC_API_SCORE_BANDS).nullable().optional(),
    assetCount: z.number().int(),
    scoredCount: z.number().int(),
    failedCount: z.number().int(),
    criteriaPassedCount: z.number().int().optional(),
    criteriaFailedCount: z.number().int().optional(),
    market: PublicScoringJobMarketSchema.nullable(),
    createdAt: z.string(),
    assets: z.array(PublicScoringJobAssetSchema),
  })
  .strict();

export type PublicAuthTokenRequest = z.infer<typeof PublicAuthTokenRequestSchema>;
export type PublicAuthTokenResponse = z.infer<typeof PublicAuthTokenResponseSchema>;
export type PublicCreateScoringJobBody = z.infer<typeof PublicCreateScoringJobBodySchema>;
export type PublicCreateScoringJobResponse = z.infer<typeof PublicCreateScoringJobResponseSchema>;
export type PublicScoringJobStatus = z.infer<typeof PublicScoringJobStatusSchema>;
export type PublicUploadConfirmResponse = z.infer<typeof PublicUploadConfirmResponseSchema>;
export type PublicUploadPresignResponse = z.infer<typeof PublicUploadPresignResponseSchema>;
export type PublicScoringJobResults = z.infer<typeof PublicScoringJobResultsSchema>;
